import { API } from 'aws-amplify';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Logger } from '@meprism/app-utils';

import { Toast } from '../../components/toast/toast';
import { InvoiceInfo, InvoiceInfoState, ManualInvoicePayload} from "./CorporateInvoiceTypes";
import { useAppSelector } from '../storeExports';

const initialState: InvoiceInfoState = {
  loading: false,
  error: null,
  data: null,
};

const fetchInvoiceInfoApi = async (code: string): Promise<InvoiceInfo> => {
  return API.get('CorpOnboarding', `/invoice/${code}/info`, {});
};

const generateManualInvoiceApi = async (code: string, payload: ManualInvoicePayload ): Promise<any> => {
  return API.post('CorpOnboarding', `/invoice/${code}/generate/manual`, {body: payload});
};

// Thunk
export const fetchInvoiceInfo = createAsyncThunk(
  'invoiceInfo/fetch',
  async (code: string, { rejectWithValue }) => {
    try {
      return await fetchInvoiceInfoApi(code);
    } catch (error: any) {
      Logger.error(`Error fetching invoice info: ${error}`);
      Toast.show({
        type: 'error',
        text1: 'Could not fetch invoice information',
      });
      return rejectWithValue(error.message ?? 'Unknown error');
    }
  }
);

export const generateManualInvoice = createAsyncThunk(
  'invoiceInfo/generateManualInvoice',
  async ({ code, payload}: { code: string; payload: ManualInvoicePayload}, { rejectWithValue }) => {
    try {
      return await generateManualInvoiceApi(code, payload);
    } catch (error: any) {
      Logger.error(`Error generating manual invoice: ${error}`);
      Toast.show({
        type: 'error',
        text1: 'Could not generate manual invoice',
      });
      return rejectWithValue(error.message ?? 'Unknown error');
    }
  }
);

// Slice
const { reducer } = createSlice({
  name: 'invoiceInfo',
  initialState,
  reducers: {
    clearInvoiceInfo: (state) => {
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoiceInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchInvoiceInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to fetch invoice info';
      });
  },
});

export const InvoiceInfoReducer = reducer;

// Selector
export const InvoiceInfoSelector = () =>
  useAppSelector((state) => ({
    invoiceInfo: state.invoiceInfo.data,
    loading: state.invoiceInfo.loading,
  }));